import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Plus, Trash2, RotateCcw, Save, Loader2 } from 'lucide-react';
import type { PipelineStageConfig } from '../../hooks/usePipelineStagesConfig';

interface PipelineSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stages: PipelineStageConfig[];
  defaults: PipelineStageConfig[];
  onSave: (stages: PipelineStageConfig[]) => Promise<boolean>;
}

function generateId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `stage_${Date.now()}`;
}

export function PipelineSettingsModal({ isOpen, onClose, stages, defaults, onSave }: PipelineSettingsModalProps) {
  const [localStages, setLocalStages] = useState<PipelineStageConfig[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalStages(stages.map((s, i) => ({ ...s, order: i + 1 })));
    }
  }, [isOpen, stages]);

  if (!isOpen) return null;

  const updateStage = (index: number, field: keyof PipelineStageConfig, value: string | number) => {
    setLocalStages(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeStage = (index: number) => {
    if (localStages.length <= 2) return;
    setLocalStages(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 })));
  };

  const addStage = () => {
    if (localStages.length >= 12) return;
    setLocalStages(prev => [...prev, {
      id: `new_stage_${Date.now()}`,
      name: '',
      description: '',
      closing_probability: 50,
      order: prev.length + 1,
    }]);
  };

  const moveStage = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= localStages.length) return;
    setLocalStages(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleSave = async () => {
    const valid = localStages.every(s => s.name.trim().length > 0);
    if (!valid) return;
    setSaving(true);
    const finalStages = localStages.map(s => ({ ...s, id: generateId(s.name) }));
    const success = await onSave(finalStages);
    setSaving(false);
    if (success) onClose();
  };

  const resetToDefaults = () => {
    setLocalStages(defaults.map((s, i) => ({ ...s, order: i + 1 })));
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col animate-in zoom-in-95"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 font-display">Pipeline Stages</h2>
            <p className="text-xs text-gray-500 mt-0.5">Configure your deal pipeline stages</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          <div className="grid grid-cols-[minmax(120px,0.8fr)_minmax(150px,1.2fr)_80px_auto] gap-2 items-center text-[11px] font-semibold text-gray-400 uppercase tracking-widest font-sans px-1 pb-1">
            <span>Stage Name</span>
            <span>Description</span>
            <span className="text-center">Close %</span>
            <span className="w-8"></span>
          </div>

          {localStages.map((stage, i) => (
            <div
              key={i}
              className="grid grid-cols-[minmax(120px,0.8fr)_minmax(150px,1.2fr)_80px_auto] gap-2 items-center group"
            >
              <input
                type="text"
                value={stage.name}
                onChange={e => updateStage(i, 'name', e.target.value)}
                placeholder="Stage name"
                className={`h-9 px-3 text-sm font-semibold rounded-lg border transition-colors focus:outline-none focus:ring-1 focus:ring-[#1875FF] focus:border-[#1875FF] ${
                  !stage.name.trim() ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 hover:bg-white'
                }`}
              />

              <input
                type="text"
                value={stage.description}
                onChange={e => updateStage(i, 'description', e.target.value)}
                placeholder="Max 5 words"
                maxLength={50}
                className="h-9 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50 hover:bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-[#1875FF] focus:border-[#1875FF]"
              />

              <input
                type="number"
                min={0}
                max={100}
                value={stage.closing_probability}
                onChange={e => updateStage(i, 'closing_probability', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                className="h-9 px-2 text-sm text-center rounded-lg border border-gray-200 bg-gray-50 hover:bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-[#1875FF] focus:border-[#1875FF]"
              />

              <button
                onClick={() => removeStage(i)}
                disabled={localStages.length <= 2}
                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-gray-300 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          {localStages.length < 12 && (
            <button
              onClick={addStage}
              className="flex items-center gap-1.5 text-sm text-[#1875FF] hover:text-[#1260d4] font-medium px-1 py-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Stage
            </button>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 rounded-b-2xl">
          <button
            onClick={resetToDefaults}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Defaults
          </button>

          <button
            onClick={handleSave}
            disabled={saving || localStages.some(s => !s.name.trim())}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#1875FF] to-[#05E0D1] hover:shadow-lg hover:shadow-blue-200/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
